import type {
  HITLActionRequest,
  HITLDecision,
  HITLRequestConfirmationMessage,
  PromptOption,
} from '@terra/shared-dto-chat-types';

export type HITLActionMessageEvent = {
  dataMessage: HITLRequestConfirmationMessage;
  selectedOption: PromptOption;
  decision: HITLDecision | undefined;
  decisions?: HITLDecision[];
};

export const getActionRequestDecisionIds = (
  actionRequest: HITLActionRequest,
) => ({
  ...(actionRequest.id ? { id: actionRequest.id } : {}),
  ...((actionRequest.toolCallId ?? actionRequest.id)
    ? { toolCallId: actionRequest.toolCallId ?? actionRequest.id }
    : {}),
});

export const getActionRequestKey = (
  actionRequest: HITLActionRequest,
  index: number,
) =>
  `${actionRequest.toolCallId ?? actionRequest.id ?? actionRequest.name}-${index}`;

export const buildBatchDecision = ({
  actionRequest,
  type,
  message,
}: {
  actionRequest: HITLActionRequest;
  type: 'approve' | 'reject';
  message?: string;
}): HITLDecision => ({
  type,
  ...getActionRequestDecisionIds(actionRequest),
  ...(type === 'reject' && message ? { message } : {}),
});

export function mergeSiblingHITLActionEvents({
  pendingHITLPartIds,
  eventsByPartId,
}: {
  pendingHITLPartIds: string[];
  eventsByPartId: Record<string, HITLActionMessageEvent>;
}): HITLActionMessageEvent | undefined {
  if (!pendingHITLPartIds.every((id) => eventsByPartId[id])) {
    return undefined;
  }

  const orderedEvents = pendingHITLPartIds.map((id) => eventsByPartId[id]);
  const decisions = orderedEvents
    .flatMap((item) => item?.decisions ?? [item?.decision])
    .filter((decision): decision is HITLDecision => !!decision);
  const rejectEvent = orderedEvents.find(
    (item) => item?.decision?.type === 'reject',
  );
  const selectedEvent = rejectEvent ?? orderedEvents[0];

  if (!selectedEvent?.decision) {
    return undefined;
  }

  return {
    dataMessage: selectedEvent.dataMessage,
    selectedOption: selectedEvent.selectedOption,
    decision: selectedEvent.decision,
    decisions,
  };
}
